import { Button, Divider, MenuItem, Stack, Typography } from '@mui/material'
import Box from '@mui/material/Box'
import { File } from '../../models/Organization'
import { calculateAge } from '../../utils/Common'
import NestedContentComponent from '../NestedContentComponent'
import { MechanicalEngineeringIcon } from '../Svgs'
import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '../../store'
import { useDispatch, useSelector } from 'react-redux'
import { AssignStatus } from '../../models/Review'
import AssignedEngineerComponent from '../AssignProject/AssignedEngineerComponent'
import { useNavigate } from 'react-router-dom'
import { architectureRoles, engineeringRoles } from '../../models/AccessControl'
import { setShowProgressModal } from '../../actions/construction'
import CustomDropdown from '../CustomDropdown'
import { useEffect, useRef, useState } from 'react'
import { deleteFileAsync } from '../../services/filesService'
import EditFile from '../Organization/EditFile'
import { showEditFileModal } from '../../actions/organization'

const images = [
  '/images/profile1.png',
  '/images/profile2.png',
  '/images/profile3.png',
  '/images/profile4.png',
  '/images/profile5.png',
]

const userSelector = createSelector(
  [(state: RootState) => state.auth],
  (auth) => auth.user,
)
const assignedItemsSelector = createSelector(
  [(state: RootState) => state.review],
  (review) => review.assignedItems,
)
const clientsSelector = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization.clients,
)
const projectsSelector = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization.projects,
)

const organizationSeletor = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization,
)

interface FileDetailsProps {
  file: File
}

const FileDetailsComponent: React.FC<FileDetailsProps> = ({ file }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch<any>()
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [buttonHeight, setButtonHeight] = useState<number>(0)
  const user = useSelector(userSelector)
  const assignedItems = useSelector(assignedItemsSelector)
  const clients = useSelector(clientsSelector)
  const projects = useSelector(projectsSelector)
  const organization = useSelector(organizationSeletor)
  const project = projects.find((project) => project.id === file.project_id)
  const client = clients.find((client) => client.id === project?.client_id)

  const handleOpenFileClick = (fileId: number) => {
    if (!!user) {
      dispatch(setShowProgressModal(true))
      if (engineeringRoles.includes(user?.role.name)) {
        navigate(`/projects/${fileId}`)
      }
      if (architectureRoles.includes(user?.role.name)) {
        navigate(`/projects/${project?.id}/files/${fileId}`)
      }
    }
  }

  const handleDeleteFileClick = (fileId: number) => {
    dispatch(deleteFileAsync({ fileId }))
    navigate('/projects')
  }

  useEffect(() => {
    const calculateHeight = () => {
      if (buttonRef.current) {
        setButtonHeight(buttonRef.current.clientHeight)
      }
    }

    calculateHeight()
    window.addEventListener('resize', calculateHeight)

    return () => {
      window.removeEventListener('resize', calculateHeight)
    }
  }, [])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        minHeight: '25rem',
        boxSizing: 'border-box',
        p: '1.25rem',
        background: '#F5F5F1',
        boxShadow: '0px 3px 5px 0px #2F2F2F1A',
        borderRadius: '0.4rem',
      }}
    >
      <Stack gap='0.75rem' sx={{ width: '100%' }}>
        <Box display='flex' justifyContent='space-between'>
          <Box display='flex' flexDirection='column' gap='0.25rem'>
            <Typography
              sx={{
                fontSize: '0.88rem',
                color: '#04002E',
                lineHeight: 'normal',
                fontWeight: 500,
              }}
            >
              {file.name}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.82rem',
                color: '#878787',
                lineHeight: 'normal',
              }}
            >
              San Diego, CA
            </Typography>
          </Box>
          <Box display='flex' flexDirection='column' gap='0.25rem'>
            <Box display='flex' gap='0.25rem'>
              <Box
                sx={{
                  background: '#878787',
                  borderRadius: '0.2rem',
                  p: '0.2rem 0.3rem',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.58rem',
                    color: '#fff',
                    lineHeight: 'normal',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                  }}
                >
                  V4
                </Typography>
              </Box>
              <Box
                sx={{
                  background: '#DD9E23',
                  borderRadius: '1rem',
                  p: '0.2rem 0.5rem',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.58rem',
                    color: '#fff',
                    lineHeight: 'normal',
                    fontWeight: 500,
                  }}
                >
                  In Progress
                </Typography>
              </Box>
            </Box>
            <Typography
              sx={{
                fontSize: '0.82rem',
                color: '#878787',
                lineHeight: 'normal',
              }}
            >
              {client?.name}
            </Typography>
          </Box>
        </Box>
        <Divider />
        <Box
          gap='0.5rem'
          sx={{
            display: 'flex',
            flexDirection: 'column',
            background: '#fff',
            borderRadius: '0.4rem',
            boxShadow: '0px 3px 5px 0px #2F2F2F1A',
            border: '1px solid #EBEBEB',
            p: '0.75rem',
          }}
        >
          <Box display='flex' justifyContent='space-between'>
            <Box
              sx={{
                display: 'flex',
                gap: '0.75rem',
              }}
            >
              <Typography
                component='div'
                sx={{
                  fontWeight: 400,
                  color: '#04002E',
                  fontSize: '0.7rem',
                }}
              >
                3 BR
              </Typography>
              <Divider flexItem orientation='vertical' sx={{ my: '2px' }} />
              <Typography
                component='div'
                sx={{
                  fontWeight: 400,
                  color: '#04002E',
                  fontSize: '0.7rem',
                }}
              >
                2 BA
              </Typography>
              <Divider flexItem orientation='vertical' sx={{ my: '2px' }} />
              <Typography
                component='div'
                sx={{
                  fontWeight: 400,
                  color: '#04002E',
                  fontSize: '0.7rem',
                }}
              >
                1 FL
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: '0.7rem',
                color: '#04002E',
                lineHeight: 'normal',
              }}
            >
              3,402 sq ft
            </Typography>
          </Box>
          <Divider />
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
          >
            <Box display='flex' position='relative' width='5rem' height='20px'>
              {images.map((image, index) => (
                <Box
                  display='flex'
                  key={index}
                  component='img'
                  src={image}
                  alt={`image-${index}`}
                  position='absolute'
                  left={`${index * 0.75}rem`}
                  zIndex={index}
                />
              ))}
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: '0.5rem',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.64rem',
                  color: '#04002E',
                  lineHeight: 'normal',
                  fontWeight: 500,
                }}
              >
                Edited {calculateAge(file.updatedAt ?? Date.now().toString())}{' '}
                ago
              </Typography>
              <Divider orientation='vertical' flexItem />
              <Typography
                sx={{
                  fontSize: '0.64rem',
                  color: '#04002E',
                  lineHeight: 'normal',
                  fontWeight: 500,
                }}
              >
                Created {calculateAge(file.createdAt ?? Date.now().toString())}{' '}
                ago
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box
          gap='0.5rem'
          sx={{
            display: 'flex',
            flexDirection: 'column',
            background: '#fff',
            borderRadius: '0.4rem',
            boxShadow: '0px 3px 5px 0px #2F2F2F1A',
            border: '1px solid #EBEBEB',
            p: '0.4rem 0.75rem',
          }}
        >
          <NestedContentComponent
            title={
              <Box
                gap='0.75rem'
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifuyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '10px',
                    background: '#1705E5',
                  }}
                ></Box>
                <Typography
                  sx={{
                    color: '#04002E',
                    fontSize: '0.76rem',
                    fontWeight: 500,
                    lineHeight: 'normal',
                  }}
                >
                  6 new alerts
                </Typography>
              </Box>
            }
            arrowColor='#04002E'
          >
            <Typography
              sx={{
                color: '#04002E',
                fontSize: '0.76rem',
                fontWeight: 500,
                lineHeight: 'normal',
              }}
            >
              Project Alerts
            </Typography>
          </NestedContentComponent>
        </Box>
        <Box
          gap='0.5rem'
          sx={{
            display: 'flex',
            flexDirection: 'column',
            background: '#fff',
            borderRadius: '0.4rem',
            boxShadow: '0px 3px 5px 0px #2F2F2F1A',
            border: '1px solid #EBEBEB',
            p: '0.4rem 0.75rem',
          }}
        >
          <NestedContentComponent
            title={
              <Box
                gap='0.75rem'
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifuyContent: 'center',
                }}
              >
                <MechanicalEngineeringIcon color='#1705E5' />
                <Typography
                  sx={{
                    color: '#04002E',
                    fontSize: '0.76rem',
                    fontWeight: 500,
                    lineHeight: 'normal',
                  }}
                >
                  Assigned Engineers
                </Typography>
              </Box>
            }
            arrowColor='#04002E'
          >
            <Box>
              {assignedItems.map(
                (assignedItem, index) =>
                  !!assignedItem &&
                  !!assignedItem.user_id &&
                  assignedItem.discipline_status ===
                    AssignStatus.InProgress && (
                    <AssignedEngineerComponent discipline={assignedItem} />
                  ),
              )}
            </Box>
          </NestedContentComponent>
        </Box>
      </Stack>
      <Box
        gap='0.75rem'
        sx={{
          display: 'flex',
          flex: 1,
          justifyContent: 'flex-end',
          alignItems: 'flex-end',
          pt: '0.75rem',
        }}
      >
        <Box>
          <CustomDropdown
            title={'Manage'}
            color='#878787'
            buttonStyleProps={{
              justifyContent: 'left',
              border: '1px solid #878787',
              borderRadius: '0.3rem',
              minWidth: 0,
              width: 'auto',
              height: `${buttonHeight - 2}px`,
              padding: '0 0.6rem',
            }}
            titleStyleProps={{
              fontSize: '0.7rem',
            }}
            hideArrow={true}
            width={120}
            menuMargin='-0.75rem 0 1rem 0'
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
          >
            <MenuItem
              onClick={() => {
                dispatch(showEditFileModal(true))
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.64rem',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: 'normal',
                  }}
                >
                  Rename
                </Typography>
              </Box>
            </MenuItem>
            <MenuItem onClick={() => handleDeleteFileClick(file.id)}>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.64rem',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: 'normal',
                  }}
                >
                  Delete
                </Typography>
              </Box>
            </MenuItem>
          </CustomDropdown>
        </Box>
        <Button
          variant='contained'
          size='small'
          ref={buttonRef}
          onClick={() => handleOpenFileClick(file.id)}
          sx={{
            background: '#56003D',
            textTransform: 'none',
            color: '#fff',
            fontSize: '0.7rem',
            fontWeight: 500,
          }}
        >
          Open File
        </Button>
      </Box>
      <EditFile
        isOpen={organization.showEditFileModal}
        handleClose={() => {
          dispatch(showEditFileModal(false))
        }}
      />
    </Box>
  )
}

export default FileDetailsComponent
